import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle } from 'lucide-react';
import SEO from '../../components/SEO';
import SeoNav from '../../components/SeoNav';
import BlogCTA from '../../components/BlogCTA';
import RelatedArticles from '../../components/RelatedArticles';

const schema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Article',
      'headline': 'UK Invoice Requirements 2025 — What Must Be on a UK Invoice?',
      'description': 'What every UK invoice must include in 2025 — for VAT-registered and non-VAT-registered businesses. Covers sole traders, limited companies, and what HMRC expects.',
      'url': 'https://www.invilic.com/blog/uk-invoice-requirements',
      'datePublished': '2026-06-01',
      'dateModified': '2026-06-01',
      'author': { '@type': 'Organization', 'name': 'Invilic', 'url': 'https://www.invilic.com' },
      'publisher': { '@type': 'Organization', 'name': 'Invilic', 'url': 'https://www.invilic.com' },
    },
    {
      '@type': 'FAQPage',
      'mainEntity': [
        {
          '@type': 'Question',
          'name': 'What must be on an invoice in the UK?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'Every UK invoice — whether you are VAT-registered or not — should include: a unique invoice number, the invoice date, your name and address, your customer\'s name and address, a description of the goods or services, the amount charged, and your payment terms. If you are VAT-registered, you must also include your VAT number, the VAT rate, the net amount, the VAT amount, and the gross total.' },
        },
        {
          '@type': 'Question',
          'name': 'Do I need to put my address on an invoice in the UK?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'Yes. Your business address (or home address if you are a sole trader working from home) should appear on every invoice. For limited companies, the Companies Act 2006 requires your registered office address on all business documents including invoices. For sole traders, your trading address or home address is sufficient.' },
        },
        {
          '@type': 'Question',
          'name': 'Do I need to add VAT to my invoice if I am not VAT-registered?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'No. If you are not VAT-registered, you must not charge VAT or show a VAT number on your invoice. Doing so is a criminal offence. Simply invoice for your service fee with no VAT line. If your turnover is approaching the £90,000 VAT registration threshold, consider registering voluntarily.' },
        },
        {
          '@type': 'Question',
          'name': 'What does a sole trader invoice need to include in the UK?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'A UK sole trader invoice needs: a unique invoice number, the date, your name (or trading name) and contact details, your customer\'s name and address, a clear description of the work done, the amount charged, and your payment terms and bank details. If VAT-registered, also include your VAT number, VAT rate, net and VAT amounts.' },
        },
        {
          '@type': 'Question',
          'name': 'Do UK invoices need to show a company registration number?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'Limited companies must display their company registration number on all business documents including invoices, under the Companies Act 2006. Sole traders and partnerships are not required to show a registration number (they have none), but must show their trading name and address.' },
        },
      ],
    },
  ],
};

const faqs = schema['@graph'][1].mainEntity;

const nonVatFields = [
  { field: 'A unique invoice number', note: 'Sequential numbering recommended — e.g. INV-2026-001' },
  { field: 'Invoice date', note: 'The date you issued the invoice' },
  { field: 'Your name (or trading name) and address', note: 'For sole traders, your legal name; or your chosen trading name alongside it' },
  { field: "Customer's name and address", note: 'Full name and address of the person or business being invoiced' },
  { field: 'Description of the goods or services', note: 'Sufficient detail to identify what was supplied' },
  { field: 'Amount charged for each item', note: 'With a subtotal if multiple items are listed' },
  { field: 'Total amount due', note: 'The final amount the customer owes' },
  { field: 'Payment terms', note: 'e.g. "Payment due within 30 days" or "Net 15"' },
  { field: 'Bank details or payment method', note: 'Sort code and account number, PayPal, or other method' },
];

const vatExtraFields = [
  { field: 'Your VAT registration number', note: 'Format: GB followed by 9 digits, e.g. GB 123 4567 89' },
  { field: 'VAT rate applied to each item', note: 'Usually 20% standard rate, or 5% / 0% for applicable supplies' },
  { field: 'Net amount (excluding VAT) per item and as a subtotal', note: null },
  { field: 'VAT amount charged (as a separate line)', note: null },
  { field: 'Gross total (including VAT)', note: null },
  { field: 'Tax point / date of supply (if different from invoice date)', note: null },
];

const limitedCompanyExtra = [
  'Full registered company name (as it appears on Companies House)',
  'Registered office address',
  'Company registration number',
  'Place of registration (e.g. "Registered in England and Wales")',
];

export default function UkInvoiceRequirementsPage() {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <SEO
        title="UK Invoice Requirements 2025 — What Must Be on a UK Invoice? | Invilic Blog"
        description="What every UK invoice must include in 2025 — for sole traders, limited companies, VAT-registered and non-VAT-registered businesses. Based on HMRC and Companies Act rules."
        canonical="https://www.invilic.com/blog/uk-invoice-requirements"
        ogType="article"
        schema={schema}
      />
      <SeoNav />

      <main className="max-w-3xl mx-auto px-6 py-16">
        <header className="mb-10">
          <div className="flex items-center gap-2 mb-5 text-sm">
            <Link to="/blog" className="text-blue-600 dark:text-blue-400 hover:underline">Blog</Link>
            <ArrowRight className="w-3 h-3 text-gray-400" />
            <span className="text-gray-500 dark:text-gray-400">UK VAT</span>
          </div>
          <span className="inline-block bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 text-xs font-semibold px-2.5 py-1 rounded-full mb-4">UK Invoicing</span>
          <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-900 dark:text-white leading-tight mb-4">
            UK Invoice Requirements 2025 — What Must Be on a UK Invoice?
          </h1>
          <p className="text-gray-500 dark:text-gray-400 text-sm">June 1, 2026 · 6 min read</p>
        </header>

        <article className="text-gray-700 dark:text-gray-300 leading-relaxed">

          <p className="text-lg mb-6">
            Every UK invoice must include your name and address, a unique invoice number, the invoice date, a description of work done, and the total amount due. If you are VAT-registered, you also need your VAT number, the VAT rate, the net amount, and the VAT charged as a separate line. Limited companies have additional Companies Act requirements.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-4">Required Fields for All UK Invoices (VAT and Non-VAT)</h2>
          <p className="mb-4">Whether you are VAT-registered or not, every invoice you send as a UK business should contain these fields:</p>
          <div className="space-y-3 mb-8">
            {nonVatFields.map(({ field, note }, i) => (
              <div key={field} className="flex items-start gap-3">
                <div className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 flex items-center justify-center text-xs font-bold mt-0.5">{i + 1}</div>
                <div className="text-sm">
                  <span className="font-medium text-gray-900 dark:text-white">{field}</span>
                  {note && <p className="text-gray-400 dark:text-gray-500 mt-0.5">{note}</p>}
                </div>
              </div>
            ))}
          </div>

          <BlogCTA
            text="Create a UK-compliant invoice with all required fields in seconds. Free, no sign-up needed."
            to="/invoice-generator-uk"
            btnText="Create UK Invoice Free"
          />

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-4">Additional Requirements for VAT-Registered Businesses</h2>
          <p className="mb-4">
            If you are registered for VAT (i.e. your taxable turnover exceeds £90,000 per year, or you have voluntarily registered), you must issue a VAT invoice for taxable supplies and include all of the above plus:
          </p>
          <div className="space-y-2 mb-6">
            {vatExtraFields.map(({ field, note }) => (
              <div key={field} className="flex items-start gap-2 text-sm">
                <CheckCircle className="w-4 h-4 text-purple-500 flex-shrink-0 mt-0.5" />
                <div>
                  <span>{field}</span>
                  {note && <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{note}</p>}
                </div>
              </div>
            ))}
          </div>
          <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-800/30 rounded-xl mb-8 text-sm">
            <strong className="font-semibold text-gray-900 dark:text-white">Not VAT-registered?</strong> Do not add a VAT line, a VAT number, or charge VAT. Simply invoice your fee with no VAT. If a client asks why there is no VAT, explain you are not VAT-registered. You are under no obligation to register until your turnover reaches £90,000.
          </div>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-4">Limited Company Invoices — Extra Requirements</h2>
          <p className="mb-4">
            Under the Companies Act 2006, UK limited companies must include specific information on all business documents — including invoices:
          </p>
          <div className="space-y-2 mb-6">
            {limitedCompanyExtra.map(item => (
              <div key={item} className="flex items-start gap-2 text-sm">
                <CheckCircle className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                <span>{item}</span>
              </div>
            ))}
          </div>
          <p className="mb-8 text-sm">
            Sole traders and partnerships have no registration number to display, but must include their trading name and a contact address. If a sole trader operates under a business name (e.g. "Jane Smith trading as JS Design"), both the trading name and the legal name must appear on invoices.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-4">Invoice Numbering in the UK</h2>
          <p className="mb-4">
            There is no legal requirement for a specific numbering format for non-VAT invoices. However, HMRC strongly recommends sequential numbering for record-keeping purposes, and VAT invoices must have a unique sequential number.
          </p>
          <p className="mb-6 text-sm">
            Common UK formats: <span className="font-mono bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded">INV-2026-001</span>, <span className="font-mono bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded">INV-001</span>, <span className="font-mono bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded">2026/001</span>. Reset per year is common and accepted — just ensure numbers remain unique.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-4">Record-Keeping Requirements</h2>
          <p className="mb-6">
            HMRC requires all businesses to keep records of sales invoices and supporting documents for at least <strong className="font-semibold text-gray-900 dark:text-white">6 years</strong>. VAT-registered businesses must keep records for 6 years from the date of the transaction. Digital copies are acceptable and mandatory for Making Tax Digital (MTD) participants.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-6">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {faqs.map(({ name, acceptedAnswer }) => (
              <details key={name} className="group border border-gray-100 dark:border-gray-800 rounded-xl overflow-hidden">
                <summary className="flex items-center justify-between px-5 py-4 cursor-pointer font-medium text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-900/50 list-none text-sm">
                  {name}
                  <ArrowRight className="w-4 h-4 text-gray-400 transition-transform group-open:rotate-90 flex-shrink-0 ml-4" />
                </summary>
                <p className="px-5 pb-4 text-sm text-gray-500 dark:text-gray-400 leading-relaxed">{acceptedAnswer.text}</p>
              </details>
            ))}
          </div>

          <BlogCTA
            text="Create a compliant UK invoice — all required fields, professional PDF, free to use."
            to="/invoice-generator-uk"
            btnText="Create UK Invoice Free"
          />

        </article>
        <RelatedArticles current="/blog/uk-invoice-requirements" />
      </main>
    </div>
  );
}
